/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, call, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Asset,
  AssetMediaType,
  AssetsApi,
  Constants,
  ContentProps,
  Ducks,
  EntryPoint,
  FileFormatToFileTypeMap,
  HideUnsubscribed,
  ParentalControlSettings,
  Recording,
  SearchUtils,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import { Selectors as PpvSelectors } from '../entitlements/assets/entitledAssets';
import { Selectors as RecordingsSelectors } from '../recordings/singleRecordings';
import AppConfig from '../../../../services/AppConfig';

function* getRecordingsClones(internalId: string): SagaIterator<Asset[]> {
  const assets: Recording[] = yield select(RecordingsSelectors.getRecordings);
  return assets.filter((asset) => asset.internalId === internalId);
}

function* getPurchasesClones(internalId: string): SagaIterator<Asset[]> {
  const assets: Asset[] = yield select(PpvSelectors.getAssets);
  return assets.filter((asset) => AssetUtils.isVod(asset) && asset.internalId === internalId);
}

function* searchAssetClones(asset: Asset): SagaIterator<Asset[]> {
  const mediaTypes = [AssetMediaType.EPG, AssetMediaType.MOVIE, AssetMediaType.EPISODE];
  const ksql = SearchUtils.createClonesFilter(asset, mediaTypes);

  if (ksql === '') {
    return [];
  }

  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  const result: Asset[] = yield Ducks.Pagination.callCompletedPaginatedApi(
    `clone_assets_${(asset as ContentProps).internalId}`,
    Constants.DEFAULT_API_PAGE_SIZE,
    AssetsApi.assetSearch,
    ksql,
    filesFormat,
    parentalControlSettings,
    mediaTypes,
    undefined,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );

  return result;
}

function* getAssetClones(asset: Asset, entryPoint?: EntryPoint): SagaIterator<Asset[]> {
  // seggregated by cache
  switch (entryPoint) {
    case EntryPoint.RECORDINGS:
    case EntryPoint.SCHEDULED_RECORDINGS:
      return yield call(getRecordingsClones, (asset as ContentProps).internalId as string);
    case EntryPoint.PURCHASES:
      return yield call(getPurchasesClones, (asset as ContentProps).internalId as string);
    default:
      break;
  }

  // aggregated search + recordings
  const [search, recordings] = yield all([
    call(searchAssetClones, asset),
    call(getRecordingsClones, (asset as ContentProps).internalId as string),
  ]);

  return [...search, ...recordings];
}

export default {
  getAssetClones,
};
